<template>
    <div>
        <div class="nk-block-head nk-block-head-sm">
            <div class="nk-block-between">
                <div class="nk-block-head-content">
                    <h3 class="nk-block-title page-title">{{trans('configuration.configuration')}}</h3>
                    <div class="nk-block-des text-soft">
                        <p>{{ trans('locale.locale') }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="admin-config-layout admin-linear-page">
            <configuration-sidebar menu="locale"></configuration-sidebar>
            <div class="admin-config-panel">
                <div class="admin-config-panel__inner admin-config-panel__inner--wide">
                    <div class="admin-linear-head">
                        <div>
                            <span class="admin-linear-head__eyebrow">Localization</span>
                            <h4 class="admin-linear-head__title">{{ trans('locale.locale') }}</h4>
                            <p class="admin-linear-head__description">Manage languages, add translation keys, and open module translations from one focused surface.</p>
                        </div>
                        <div class="admin-linear-stats">
                            <div>
                                <span>{{ trans('locale.locale_list') }}</span>
                                <strong>{{ locales && locales.total ? locales.total : 0 }}</strong>
                            </div>
                            <div>
                                <span>{{ trans('locale.module') }}</span>
                                <strong>{{ moduleCount }}</strong>
                            </div>
                        </div>
                    </div>
                    <show-tip module="locale" tip="tip_locale"></show-tip>

                    <section class="admin-config-list-section" aria-labelledby="admin-config-locale-list-title">
                    <header class="admin-config-list-head admin-config-list-head--split">
                        <div class="admin-config-list-head__text">
                            <h4 id="admin-config-locale-list-title" class="admin-config-list-head__title">
                                {{trans('locale.locale_list')}}
                            </h4>
                            <p
                                v-if="locales && locales.total"
                                class="admin-config-list-head__sub"
                                role="status"
                            >
                                {{trans('general.total_result_found',{count:locales.total})}}
                            </p>
                            <p
                                v-else-if="locales"
                                class="admin-config-list-head__sub admin-config-list-head__sub--empty"
                                role="status"
                            >
                                {{trans('general.no_result_found')}}
                            </p>
                        </div>
                        <div class="admin-config-list-head__actions">
                            <button
                                type="button"
                                class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--outline"
                                v-b-modal.modal-locale-add-word
                            >
                                {{ trans('locale.add_new_word') }}
                            </button>
                            <button
                                type="button"
                                class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid"
                                v-b-modal.modal-locale-create
                            >
                                <admin-icon name="plus"></admin-icon> {{ trans('general.add_new') }}
                            </button>
                        </div>
                    </header>

                    <div class="admin-list-table-card admin-config-list-card" v-if="locales && locales.total">
                        <div class="table-responsive">
                            <table class="table admin-data-table admin-data-table--config-list admin-linear-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">{{trans('general.name')}}</th>
                                        <th scope="col">{{trans('locale.locale')}}</th>
                                        <th scope="col" class="admin-table-actions-cell">{{trans('general.actions')}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="locale in locales.data" :key="locale.id">
                                        <td>
                                            <strong class="admin-linear-primary">{{ locale.name }}</strong>
                                        </td>
                                        <td>
                                            <span class="admin-linear-code">{{ locale.locale }}</span>
                                        </td>
                                        <td class="admin-table-actions-cell">
                                            <table-row-action-dropdown>
                                                <b-dropdown-item-button @click="editLocale(locale)">
                                                    <admin-icon name="pencil"></admin-icon> {{ trans('general.edit') }}
                                                </b-dropdown-item-button>
                                                <b-dropdown-item :to="'/configuration/locale/'+locale.locale+'/auth'">
                                                    <admin-icon name="eye"></admin-icon> {{ trans('auth.authentication') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item-button
                                                    v-if="locale.locale !== 'en'"
                                                    v-confirm="{ok: confirmDelete(locale)}"
                                                    class="admin-row-actions__danger"
                                                >
                                                    <admin-icon name="trash"></admin-icon> {{ trans('general.delete') }}
                                                </b-dropdown-item-button>
                                            </table-row-action-dropdown>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="admin-table-pagination-wrap">
                            <pagination-record :page-length.sync="filterLocaleForm.page_length" :records="locales" @updateRecords="getLocales" @change.native="getLocales"></pagination-record>
                        </div>
                    </div>
                    <module-info v-if="locales && !locales.total" module="locale" title="module_info_title" description="module_info_description" icon="globe"></module-info>
                    </section>
                </div>
            </div>
        </div>

        <b-modal
            id="modal-locale-create"
            hide-footer
            centered
            lazy
            modal-class="admin-modal-pro"
            content-class="admin-modal-pro__content"
        >
            <template #modal-title>{{trans('locale.add_new_locale')}}</template>
            <locale-form @completed="getLocales" @cancel="$bvModal.hide('modal-locale-create')"></locale-form>
        </b-modal>

        <b-modal
            id="modal-locale-add-word"
            hide-footer
            centered
            lazy
            modal-class="admin-modal-pro"
            content-class="admin-modal-pro__content"
        >
            <template #modal-title>
                {{trans('locale.add_new_word')}}
                <show-tip module="locale" tip="tip_add_word" type="field"></show-tip>
            </template>
            <form class="admin-linear-form" @submit.prevent="addWord" @keydown="addWordForm.errors.clear($event.target.name)">
                <div class="admin-linear-field">
                    <label>{{trans('locale.word')}}</label>
                    <input class="form-control" type="text" value="" v-model="addWordForm.word" name="word" :placeholder="trans('locale.word')">
                    <show-error :form-name="addWordForm" prop-name="word"></show-error>
                </div>
                <div class="admin-linear-field">
                    <label>{{trans('locale.module')}}</label>
                    <v-select v-model="addWordForm.module" name="module" id="module" :options="modules" :placeholder="trans('locale.select_module')" @select="addWordForm.errors.clear('module')" @remove="addWordForm.module = ''"></v-select>
                    <show-error :form-name="addWordForm" prop-name="module"></show-error>
                </div>
                <div class="admin-linear-field">
                    <label>{{trans('locale.translation')}}</label>
                    <input class="form-control" type="text" value="" v-model="addWordForm.translation" name="translation" :placeholder="trans('locale.translation')">
                    <show-error :form-name="addWordForm" prop-name="translation"></show-error>
                </div>
                <div class="admin-linear-form__actions">
                    <button type="button" class="btn btn-sm admin-form-btn-cancel" @click="$bvModal.hide('modal-locale-add-word')">
                        {{trans('general.cancel')}}
                    </button>
                    <button type="submit" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid">
                        <span>{{trans('general.save')}}</span>
                    </button>
                </div>
            </form>
        </b-modal>
    </div>
</template>


<script>
    import configurationSidebar from '../system-config-sidebar'
    import localeForm from './form'
    import vSelect from 'vue-multiselect'

    export default {
        components : { configurationSidebar,localeForm,vSelect },
        data() {
            return {
                locales: {
                    total: 0,
                    data: []
                },
                filterLocaleForm: {
                    page_length: helper.getConfig('page_length')
                },
                addWordForm: new Form({
                    word: '',
                    translation: '',
                    module: ''
                }),
                modules: []
            };
        },
        mounted(){
            if(!helper.hasPermission('access-configuration')){
                helper.notAccessibleMsg();
                this.$router.push('/home');
            }

            if(!helper.featureAvailable('multilingual')){
                helper.featureNotAvailableMsg();
                this.$router.push('/home');
            }

            this.getLocales();
        },
        computed: {
            moduleCount() {
                return Array.isArray(this.modules) ? this.modules.length : _size(this.modules);
            }
        },
        methods: {
            getLocales(page){
                if (typeof page !== 'number') {
                    page = 1;
                }
                this.$bvModal.hide('modal-locale-create');
                let url = helper.getFilterURL(this.filterLocaleForm);
                axios.get('/api/locale?page=' + page + url)
                    .then(response => response.data)
                    .then(response => {
                        this.locales = response.locales;
                        this.modules = response.modules;
                    })
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            editLocale(locale){
                this.$router.push('/configuration/locale/'+locale.id+'/edit');
            },
            confirmDelete(locale){
                return dialog => this.deleteLocale(locale);
            },
            deleteLocale(locale){
                axios.delete('/api/locale/'+locale.id)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.getLocales();
                    }).catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            addWord(){
                this.addWordForm.post('/api/locale/add-word')
                    .then(response => {
                        toastr.success(response.message)
                        this.$bvModal.hide('modal-locale-add-word');
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                    });
            }
        }
    }
</script>

<style scoped>
.admin-linear-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
    padding: 1.1rem 1.2rem;
    border: 1px solid var(--dash-pro-border);
    border-radius: var(--dash-pro-radius-sm);
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.06), var(--dash-pro-surface));
}

.admin-linear-head__eyebrow {
    display: block;
    color: var(--lms-primary);
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.admin-linear-head__title {
    margin: 0.35rem 0 0;
    color: var(--dash-pro-text);
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.04em;
}

.admin-linear-head__description {
    max-width: 42rem;
    margin: 0.45rem 0 0;
    color: var(--dash-pro-muted);
    font-size: 0.875rem;
    line-height: 1.55;
}

.admin-linear-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(6rem, 1fr));
    min-width: 16rem;
    border: 1px solid var(--dash-pro-border);
    border-radius: var(--dash-pro-radius-sm);
    background: var(--dash-pro-surface);
}

.admin-linear-stats > div {
    padding: 0.85rem 1rem;
    border-right: 1px solid var(--dash-pro-border);
}

.admin-linear-stats > div:last-child {
    border-right: 0;
}

.admin-linear-stats span {
    display: block;
    color: var(--dash-pro-muted);
    font-size: 0.72rem;
    font-weight: 700;
}

.admin-linear-stats strong {
    display: block;
    margin-top: 0.25rem;
    color: var(--dash-pro-text);
    font-size: 1.65rem;
    letter-spacing: -0.06em;
}

.admin-linear-form {
    display: grid;
    gap: 1rem;
}

.admin-linear-field label {
    display: block;
    margin-bottom: 0.45rem;
    color: var(--dash-pro-text);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.admin-linear-form__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.65rem;
    padding-top: 0.35rem;
}

.admin-linear-primary {
    color: var(--dash-pro-text);
    font-weight: 650;
}

.admin-linear-code {
    display: inline-flex;
    align-items: center;
    min-height: 1.65rem;
    padding: 0.15rem 0.55rem;
    border: 1px solid var(--dash-pro-border);
    border-radius: 999px;
    color: var(--dash-pro-text-secondary);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

@media (max-width: 767.98px) {
    .admin-linear-head {
        align-items: stretch;
        flex-direction: column;
    }

    .admin-linear-stats {
        min-width: 0;
    }
}
</style>
